import { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

/**
 * PerformanceTrends — Tabbed single chart.
 * Tabs: Stability (crash-free + ANR) | Performance (startup) | Engagement (sessions + users)
 * One clean chart per tab. Period selector: 7d / 30d / 90d.
 */

const TABS = [
  { key: 'stability', label: 'Stability' },
  { key: 'performance', label: 'Performance' },
  { key: 'engagement', label: 'Engagement' },
];

const PERIODS = [7, 30, 90];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-800 border border-surface-600 rounded-lg p-2.5 shadow-lg text-xs">
      <p className="text-gray-400 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }} className="font-medium">
          {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}
          {entry.name.includes('%') || entry.name.includes('Rate') ? '%' : ''}
          {entry.name.includes('ms') ? 'ms' : ''}
        </p>
      ))}
    </div>
  );
}

export default function PerformanceTrends({ trend = [], trendDays = 30, onChangeDays }) {
  const [activeTab, setActiveTab] = useState('stability');

  const data = trend.map(d => ({
    date: new Date(d.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
    crashFree: parseFloat(d.crash_free_pct) || 0,
    anrRate: parseFloat(d.anr_rate) || 0,
    startupP90: parseInt(d.startup_p90) || 0,
    healthScore: parseFloat(d.health_score) || 0,
    sessions: parseInt(d.sessions) || 0,
    users: parseInt(d.users) || 0,
  }));

  const renderChart = () => {
    if (data.length === 0) {
      return (
        <div className="flex items-center justify-center h-48 text-gray-500 text-sm">
          No trend data available
        </div>
      );
    }

    switch (activeTab) {
      case 'stability':
        return (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data}>
              <defs>
                <linearGradient id="gradCrashFree" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2e3354" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#6b7280' }} />
              <YAxis domain={['auto', 'auto']} tick={{ fontSize: 10, fill: '#6b7280' }} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="crashFree" name="Crash-Free %" stroke="#10b981" fill="url(#gradCrashFree)" strokeWidth={2} />
              <Line type="monotone" dataKey="anrRate" name="ANR Rate %" stroke="#f59e0b" strokeWidth={1.5} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        );
      case 'performance':
        return (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data}>
              <defs>
                <linearGradient id="gradStartup" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2e3354" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#6b7280' }} />
              <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="startupP90" name="Startup P90 ms" stroke="#6366f1" fill="url(#gradStartup)" strokeWidth={2} />
              <Line type="monotone" dataKey="healthScore" name="Health Score" stroke="#10b981" strokeWidth={1.5} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        );
      case 'engagement':
        return (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data}>
              <defs>
                <linearGradient id="gradSessions" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2e3354" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#6b7280' }} />
              <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="sessions" name="Sessions" stroke="#3b82f6" fill="url(#gradSessions)" strokeWidth={2} />
              <Line type="monotone" dataKey="users" name="Users" stroke="#8b5cf6" strokeWidth={1.5} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-surface-700/40 rounded-xl border border-surface-600/50 overflow-hidden">
      {/* Header: Tabs + Period selector */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="flex items-center gap-1">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                activeTab === tab.key
                  ? 'bg-surface-600 text-gray-100'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          {PERIODS.map((d) => (
            <button
              key={d}
              onClick={() => onChangeDays?.(d)}
              className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${
                trendDays === d
                  ? 'bg-brand-500/20 text-brand-400'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Chart area */}
      <div className="px-2 pb-3">
        {renderChart()}
      </div>
    </div>
  );
}
